import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  PhoneXMarkIcon,
  MicrophoneIcon,
  BuildingStorefrontIcon,
  PhoneIcon,
  EnvelopeIcon,
  BriefcaseIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import { leadService } from '../../services/leadService';
import { panelNeedsDisposition, shouldAutoExpandCallPanel } from './callPanelVisibility';
import { RemoteAudioReading } from '../../contexts/remoteAudioMonitor';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface CallLeadInfo {
  leadId: string | null;
  leadName: string | null;
  leadPhone: string | null;
  leadEmail: string | null;
  companyName: string | null;
  leadNotes: string | null;
  campaignName: string | null;
  campaignId: string | null;
  callAttemptId: string | null;
  scriptName?: string | null;
  scriptContent?: string | null;
  scriptStructure?: Record<string, any> | null;
  leadStatus?: string | null;
  subStageId?: string | null;
  liveConnect?: boolean;
}

export type DispositionValue =
  | 'answered'
  | 'interested'
  | 'not_interested'
  | 'no_answer'
  | 'voicemail'
  | 'hangup'
  | 'app_in'
  | 'app_out'
  | 'out_of_business'
  | 'unqualified'
  | 'wrong_number'
  | 'follow_up'
  | 'gatekeeper'
  | 'operator'
  | 'do_not_call'
  | 'spanish';

interface DispositionOption {
  value: DispositionValue;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  hoverColor: string;
}

export type CallDispositionMode = 'results_callback' | 'main_dial' | 'full';

interface InCallPopupProps {
  /** results_callback = View Results (call history only); main_dial = manual lead list */
  dispositionMode?: CallDispositionMode;
  /** Whether the agent is currently on a call */
  isActive: boolean;
  /** Lead/call info to display */
  leadInfo: CallLeadInfo | null;
  /** Call duration in seconds */
  durationSeconds: number;
  /** Whether the call is muted */
  isMuted: boolean;
  /** Whether the popup is minimized */
  isMinimized?: boolean;
  /** Called when the agent clicks Hang Up */
  onHangUp: () => void;
  /** Called when the agent toggles mute */
  onToggleMute: () => void;
  /** Called when the agent selects a disposition with optional notes */
  onDisposition: (
    value: DispositionValue,
    notes?: string,
    followUpDate?: string,
    extras?: {
      subStageId?: string;
      mainStatusChange?: string;
      followUpOnly?: boolean;
      callHistoryOnly?: boolean;
    }
  ) => void;
  /** Whether a disposition was already submitted */
  dispositionSubmitted?: DispositionValue | null;
  /** Whether voicemail was detected by AMD */
  voicemailDetected?: boolean;
  /** Live inbound audio health, so silence has a visible cause */
  remoteAudio?: RemoteAudioReading | null;
  /** Call ended but agent has not chosen a disposition yet */
  awaitingDisposition?: boolean;
  /** Parked dispositions from a previous call accepted during wrap-up */
  pendingDispositionCount?: number;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const RESULTS_CALLBACK_DISPOSITIONS: DispositionOption[] = [
  {
    value: 'answered',
    label: 'Answer',
    icon: '✅',
    color: 'text-green-700 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800',
    hoverColor: 'hover:bg-green-100 dark:hover:bg-green-900/50',
  },
  {
    value: 'no_answer',
    label: 'No Answer',
    icon: '📞',
    color: 'text-orange-700 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800',
    hoverColor: 'hover:bg-orange-100 dark:hover:bg-orange-900/50',
  },
  {
    value: 'voicemail',
    label: 'Voicemail',
    icon: '📭',
    color: 'text-orange-700 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800',
    hoverColor: 'hover:bg-orange-100 dark:hover:bg-orange-900/50',
  },
];

const dispositions: DispositionOption[] = [
  {
    value: 'interested',
    label: 'Interested',
    icon: '👍',
    color: 'text-green-700 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800',
    hoverColor: 'hover:bg-green-100 dark:hover:bg-green-900/50',
  },
  {
    value: 'not_interested',
    label: 'Not Interested',
    icon: '👎',
    color: 'text-red-700 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800',
    hoverColor: 'hover:bg-red-100 dark:hover:bg-red-900/50',
  },
  {
    value: 'out_of_business',
    label: 'Out of Business',
    icon: '🏚️',
    color: 'text-gray-700 dark:text-gray-400',
    bgColor: 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700',
    hoverColor: 'hover:bg-gray-100 dark:hover:bg-gray-800/70',
  },
  {
    value: 'unqualified',
    label: 'Unqualified',
    icon: '⛔',
    color: 'text-orange-700 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800',
    hoverColor: 'hover:bg-orange-100 dark:hover:bg-orange-900/50',
  },
  {
    value: 'wrong_number',
    label: 'Wrong Number',
    icon: '📵',
    color: 'text-yellow-700 dark:text-yellow-400',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800',
    hoverColor: 'hover:bg-yellow-100 dark:hover:bg-yellow-900/50',
  },
  {
    value: 'follow_up',
    label: 'Follow Up',
    icon: '📅',
    color: 'text-blue-700 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800',
    hoverColor: 'hover:bg-blue-100 dark:hover:bg-blue-900/50',
  },
  {
    value: 'no_answer',
    label: 'No Answer',
    icon: '📞',
    color: 'text-orange-700 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800',
    hoverColor: 'hover:bg-orange-100 dark:hover:bg-orange-900/50',
  },
  {
    value: 'voicemail',
    label: 'Voicemail',
    icon: '📭',
    color: 'text-orange-700 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800',
    hoverColor: 'hover:bg-orange-100 dark:hover:bg-orange-900/50',
  },
  {
    value: 'hangup',
    label: 'Hang Up',
    icon: '📴',
    color: 'text-yellow-700 dark:text-yellow-400',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800',
    hoverColor: 'hover:bg-yellow-100 dark:hover:bg-yellow-900/50',
  },
  {
    value: 'app_out',
    label: 'App Out',
    icon: '📱',
    color: 'text-indigo-700 dark:text-indigo-400',
    bgColor: 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-800',
    hoverColor: 'hover:bg-indigo-100 dark:hover:bg-indigo-900/50',
  },
  {
    value: 'app_in',
    label: 'App In',
    icon: '📲',
    color: 'text-sky-700 dark:text-sky-400',
    bgColor: 'bg-sky-50 dark:bg-sky-900/30 border-sky-200 dark:border-sky-800',
    hoverColor: 'hover:bg-sky-100 dark:hover:bg-sky-900/50',
  },
  {
    value: 'gatekeeper',
    label: 'Gatekeeper',
    icon: '🛡️',
    color: 'text-amber-700 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800',
    hoverColor: 'hover:bg-amber-100 dark:hover:bg-amber-900/50',
  },
  {
    value: 'operator',
    label: 'Operator',
    icon: '🎧',
    color: 'text-cyan-700 dark:text-cyan-400',
    bgColor: 'bg-cyan-50 dark:bg-cyan-900/30 border-cyan-200 dark:border-cyan-800',
    hoverColor: 'hover:bg-cyan-100 dark:hover:bg-cyan-900/50',
  },
  {
    value: 'spanish',
    label: 'Spanish',
    icon: '🇪🇸',
    color: 'text-rose-700 dark:text-rose-400',
    bgColor: 'bg-rose-50 dark:bg-rose-900/30 border-rose-200 dark:border-rose-800',
    hoverColor: 'hover:bg-rose-100 dark:hover:bg-rose-900/50',
  },
  {
    value: 'do_not_call',
    label: 'Do Not Call',
    icon: '🚫',
    color: 'text-red-700 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800',
    hoverColor: 'hover:bg-red-100 dark:hover:bg-red-900/50',
  },
];

const PIPELINE_MAIN_STATUSES = [
  'interested',
  'follow_up',
  'not_interested',
  'unqualified',
  'app_in',
  'app_out',
  'submitted',
  'contract',
  'funded',
] as const;

type PipelineMainStatus = (typeof PIPELINE_MAIN_STATUSES)[number];

const MAIN_STATUS_LABELS: Record<PipelineMainStatus, string> = {
  interested: 'Interested',
  follow_up: 'Follow Up',
  not_interested: 'Not Interested',
  unqualified: 'Unqualified',
  app_in: 'App In',
  app_out: 'App Out',
  submitted: 'Submitted',
  contract: 'Contract',
  funded: 'Funded',
};

const isPipelineMainStatus = (value: string | null | undefined): value is PipelineMainStatus =>
  !!value && PIPELINE_MAIN_STATUSES.includes(value as PipelineMainStatus);

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDuration(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

const FOLLOW_UP_TIME_OPTIONS: string[] = (() => {
  const slots: string[] = [];
  for (let hour = 6; hour <= 21; hour += 1) {
    for (let minute = 0; minute < 60; minute += 15) {
      slots.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    }
  }
  return slots;
})();

const formatTimeLabel = (value: string) => {
  const [h, m] = value.split(':').map((part) => Number(part));
  if (Number.isNaN(h) || Number.isNaN(m)) return value;
  const date = new Date();
  date.setHours(h, m, 0, 0);
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

const applyTimeToDate = (date: Date, timeValue: string): Date => {
  const [h, m] = timeValue.split(':').map((part) => Number(part));
  const next = new Date(date);
  next.setHours(Number.isNaN(h) ? 10 : h, Number.isNaN(m) ? 0 : m, 0, 0);
  return next;
};

function formatPhone(phone: string | null): string {
  if (!phone) return 'Unknown';
  if (phone.startsWith('+1') && phone.length === 12) {
    return `(${phone.slice(2, 5)}) ${phone.slice(5, 8)}-${phone.slice(8)}`;
  }
  return phone;
}

/**
 * What the line is doing, in the header.
 *
 * This replaces a "👤 Human" badge that was shown on every connected call.
 * AMD is off for human campaigns, so no human verdict exists to show — the
 * badge asserted one anyway, on calls that were three-quarters voicemail. A
 * meter of the audio actually arriving says something true instead, and says
 * it about the exact complaint agents raise: the call connected and nobody
 * is there.
 */
const LineAudioChip: React.FC<{ reading: RemoteAudioReading }> = ({ reading }) => {
  if (reading.state === 'no-media') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-red-600 text-white">
        ⚠ No audio path
      </span>
    );
  }

  if (reading.state === 'silent') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-amber-500/90 text-white">
        🔇 No sound {Math.floor(reading.quietForMs / 1000)}s
      </span>
    );
  }

  // Live and warming-up both get the meter: three bars lit by how loud the far
  // end is right now, so an agent can see the line working before anyone talks.
  const bars = reading.level >= 0.12 ? 3 : reading.level >= 0.05 ? 2 : reading.level >= 0.02 ? 1 : 0;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-white/20 text-green-100"
      title="Audio is arriving from the merchant"
    >
      <span className="flex items-end gap-[2px] h-2.5" aria-hidden>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={`w-[3px] rounded-sm transition-all duration-150 ${
              i < bars ? 'bg-green-200' : 'bg-white/30'
            }`}
            style={{ height: `${5 + i * 3}px` }}
          />
        ))}
      </span>
      Line audio
    </span>
  );
};

// ─── Component ──────────────────────────────────────────────────────────────

const InCallPopup: React.FC<InCallPopupProps> = ({
  dispositionMode = 'full',
  isActive,
  leadInfo,
  durationSeconds,
  isMuted,
  onHangUp,
  onToggleMute,
  onDisposition,
  dispositionSubmitted,
  voicemailDetected,
  remoteAudio = null,
  awaitingDisposition = false,
  pendingDispositionCount = 0,
}) => {
  const isResultsCallback = dispositionMode === 'results_callback';
  const isMainDial = dispositionMode === 'main_dial';
  const visibleDispositions = isResultsCallback
    ? RESULTS_CALLBACK_DISPOSITIONS
    : dispositions;
  const [minimized, setMinimized] = useState(false);
  const [showDispositions, setShowDispositions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showFollowUpDatePicker, setShowFollowUpDatePicker] = useState(false);
  const [followUpDate, setFollowUpDate] = useState<Date | null>(null);
  const [followUpTime, setFollowUpTime] = useState('10:00');
  const [editableCompanyName, setEditableCompanyName] = useState('');
  const [editableLeadName, setEditableLeadName] = useState('');
  const [editableLeadEmail, setEditableLeadEmail] = useState('');
  const [originalCompanyName, setOriginalCompanyName] = useState('');
  const [originalLeadName, setOriginalLeadName] = useState('');
  const [originalLeadEmail, setOriginalLeadEmail] = useState('');
  const [leadNotesDraft, setLeadNotesDraft] = useState('');
  const [originalLeadNotes, setOriginalLeadNotes] = useState('');
  const [savingLeadDetails, setSavingLeadDetails] = useState(false);
  const [editingField, setEditingField] = useState<'company' | 'contact' | 'email' | null>(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [subStages, setSubStages] = useState<Array<{ id: string; name: string }>>([]);
  const [mainStatusChange, setMainStatusChange] = useState('');
  const popupRef = useRef<HTMLDivElement>(null);
  const wasActiveRef = useRef(false);

  // Identity of the call the panel is showing. Minimizing is a choice about THIS
  // call, so it has to survive the isActive/awaitingDisposition toggles that fire
  // repeatedly while the same call runs — otherwise the panel re-expands on its
  // own and the agent has no way to get it out of the way but a page reload.
  // `undefined` means "never auto-expanded yet", so the first open still happens
  // even when the call carries no lead info (callKey null).
  const callKey = leadInfo?.callAttemptId || leadInfo?.leadId || null;
  const autoExpandedForRef = useRef<string | null | undefined>(undefined);

  // Always show dispositions (during AND after call) so agent can pre-select
  useEffect(() => {
    if (awaitingDisposition) {
      setShowDispositions(true);
      if (shouldAutoExpandCallPanel({ autoExpandedFor: autoExpandedForRef.current, callKey })) {
        autoExpandedForRef.current = callKey;
        setMinimized(false);
      }
    }
  }, [awaitingDisposition, callKey]);

  // Reset state when call becomes active
  useEffect(() => {
    if (isActive) {
      setShowDispositions(true);
      if (shouldAutoExpandCallPanel({ autoExpandedFor: autoExpandedForRef.current, callKey })) {
        autoExpandedForRef.current = callKey;
        setMinimized(false);
      }
      if (!awaitingDisposition) {
        setFollowUpDate(null);
        setFollowUpTime('10:00');
        setShowFollowUpDatePicker(false);
      }
      setMainStatusChange('');
      wasActiveRef.current = true;
    } else if (wasActiveRef.current) {
      // Call just ended — keep popup visible briefly (dispositions already shown)
      setShowDispositions(true);
      wasActiveRef.current = false;
    }
    }, [isActive, awaitingDisposition]);

  useEffect(() => {
    const company = leadInfo?.companyName || '';
    const name = leadInfo?.leadName || '';
    const email = leadInfo?.leadEmail || '';
    const notes = leadInfo?.leadNotes || '';
    setEditableCompanyName(company);
    setEditableLeadName(name);
    setEditableLeadEmail(email);
    setLeadNotesDraft(notes);
    setOriginalCompanyName(company);
    setOriginalLeadName(name);
    setOriginalLeadEmail(email);
    setOriginalLeadNotes(notes);
    setEditingNotes(false);
    setMainStatusChange('');
  }, [leadInfo?.leadId, leadInfo?.companyName, leadInfo?.leadName, leadInfo?.leadEmail, leadInfo?.leadNotes, leadInfo?.leadStatus]);

  const effectiveMainStatus = useMemo(() => {
    if (isPipelineMainStatus(mainStatusChange)) return mainStatusChange;
    if (isPipelineMainStatus(leadInfo?.leadStatus)) return leadInfo.leadStatus;
    return '';
  }, [mainStatusChange, leadInfo?.leadStatus]);

  useEffect(() => {
    if (!leadInfo?.campaignId || !effectiveMainStatus) {
      setSubStages([]);
      return;
    }
    leadService
      .getSubStages({ campaignId: leadInfo.campaignId, mainStatus: effectiveMainStatus })
      .then(setSubStages)
      .catch(() => setSubStages([]));
  }, [leadInfo?.campaignId, effectiveMainStatus]);

  if (!isActive && !showDispositions && !dispositionSubmitted && !awaitingDisposition) return null;

  const normalizedOriginalCompany = originalCompanyName.trim();
  const normalizedOriginalName = originalLeadName.trim();
  const normalizedOriginalEmail = originalLeadEmail.trim();
  const normalizedOriginalNotes = originalLeadNotes.trim();
  const normalizedCurrentCompany = editableCompanyName.trim();
  const normalizedCurrentName = editableLeadName.trim();
  const normalizedCurrentEmail = editableLeadEmail.trim();
  const normalizedCurrentNotes = leadNotesDraft.trim();
  const hasLeadDetailEdits =
    normalizedCurrentCompany !== normalizedOriginalCompany ||
    normalizedCurrentName !== normalizedOriginalName ||
    normalizedCurrentEmail !== normalizedOriginalEmail ||
    normalizedCurrentNotes !== normalizedOriginalNotes;

  const handleDisposition = async (
    value: DispositionValue,
    extras?: {
      subStageId?: string;
      mainStatusChange?: string;
      followUpOnly?: boolean;
      callHistoryOnly?: boolean;
    }
  ) => {
    if (isResultsCallback) {
      setSubmitting(true);
      try {
        await onDisposition(value, undefined, undefined, { callHistoryOnly: true });
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (value === 'follow_up' && !extras?.followUpOnly) {
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 1);
      defaultDate.setHours(10, 0, 0, 0);
      setFollowUpTime('10:00');
      setFollowUpDate(defaultDate);
      setShowFollowUpDatePicker(true);
      return;
    }
    setSubmitting(true);
    try {
      await onDisposition(value, undefined, undefined, {
        ...extras,
        // Main Stage dropdown applies only to sub-stage picks — not disposition buttons.
        mainStatusChange: extras?.mainStatusChange,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubStageSelect = async (subStageId: string) => {
    if (!effectiveMainStatus) {
      toast.error('Please select a main status first');
      return;
    }
    setSubmitting(true);
    try {
      await onDisposition(effectiveMainStatus as DispositionValue, undefined, undefined, {
        subStageId,
        mainStatusChange: mainStatusChange || undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleFollowUpConfirm = async (followUpOnly = false) => {
    if (!followUpDate) {
      toast.error('Please select a follow-up date and time');
      return;
    }
    if (followUpDate.getTime() < Date.now()) {
      toast.error('Follow-up date must be in the future');
      return;
    }
    setSubmitting(true);
    try {
      await onDisposition('follow_up', undefined, followUpDate.toISOString(), { followUpOnly });
    } finally {
      setSubmitting(false);
      setShowFollowUpDatePicker(false);
      setFollowUpDate(null);
      setFollowUpTime('10:00');
    }
  };

  const handleFollowUpCalendarChange = (date: Date | null) => {
    if (!date) {
      setFollowUpDate(null);
      return;
    }
    setFollowUpDate(applyTimeToDate(date, followUpTime));
  };

  const handleFollowUpTimeChange = (timeValue: string) => {
    setFollowUpTime(timeValue);
    if (!followUpDate) return;
    setFollowUpDate(applyTimeToDate(followUpDate, timeValue));
  };

  const followUpModal =
    !isResultsCallback && showFollowUpDatePicker ? (
      <div
        className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 p-4"
        onClick={() => {
          setShowFollowUpDatePicker(false);
          setFollowUpDate(null);
          setFollowUpTime('10:00');
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="schedule-follow-up-title"
          className="bg-card border border-border rounded-2xl shadow-2xl p-5 w-full max-w-[400px] max-h-[90vh] overflow-y-auto animate-in zoom-in-95 fade-in duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl" aria-hidden>
              📅
            </span>
            <h3 id="schedule-follow-up-title" className="text-base font-semibold text-foreground">
              Schedule Follow Up
            </h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Choose a date, then pick a time.</p>

          <div className="follow-up-datepicker flex justify-center mb-4">
            <DatePicker
              selected={followUpDate}
              onChange={handleFollowUpCalendarChange}
              minDate={new Date()}
              inline
              calendarClassName="follow-up-calendar"
            />
          </div>

          <label className="block text-xs font-medium text-muted-foreground mb-1.5">Time</label>
          <select
            className="input w-full text-sm mb-4"
            value={followUpTime}
            onChange={(e) => handleFollowUpTimeChange(e.target.value)}
          >
            {FOLLOW_UP_TIME_OPTIONS.map((slot) => (
              <option key={slot} value={slot}>
                {formatTimeLabel(slot)}
              </option>
            ))}
          </select>

          {followUpDate && (
            <p className="text-xs text-muted-foreground mb-4 rounded-lg bg-muted/40 px-3 py-2">
              Scheduled:{' '}
              <span className="font-medium text-foreground">
                {followUpDate.toLocaleString(undefined, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </span>
            </p>
          )}

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => {
                setShowFollowUpDatePicker(false);
                setFollowUpDate(null);
                setFollowUpTime('10:00');
              }}
              className="w-full px-3 py-2.5 text-sm font-medium rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!followUpDate || submitting}
              onClick={() => void handleFollowUpConfirm(false)}
              className="w-full px-3 py-2.5 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Set Follow Up
            </button>
            <button
              type="button"
              disabled={!followUpDate || submitting}
              onClick={() => void handleFollowUpConfirm(true)}
              className="w-full px-3 py-2.5 text-sm font-medium rounded-lg border border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Date Only
            </button>
          </div>
        </div>
      </div>
    ) : null;

  const handleSaveLeadDetails = async () => {
    if (!leadInfo?.leadId || !hasLeadDetailEdits) return;
    setSavingLeadDetails(true);
    try {
      await leadService.updateLead(leadInfo.leadId, {
        companyName: normalizedCurrentCompany || undefined,
        fullName: normalizedCurrentName || undefined,
        email: normalizedCurrentEmail || undefined,
        notes: normalizedCurrentNotes,
      });
      setOriginalCompanyName(normalizedCurrentCompany);
      setOriginalLeadName(normalizedCurrentName);
      setOriginalLeadEmail(normalizedCurrentEmail);
      setOriginalLeadNotes(normalizedCurrentNotes);
      setEditingNotes(false);
      toast.success('Lead details saved');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save lead details');
    } finally {
      setSavingLeadDetails(false);
    }
  };

  // ─── Minimized view ────────────────────────────────────────────────
  // The panel can now stay minimized through wrap-up, so the pill must not look
  // like a live call when what it really wants is a disposition.
  const needsDisposition = panelNeedsDisposition({ awaitingDisposition, isActive });

  if (minimized) {
    return (
      <>
        {followUpModal && createPortal(followUpModal, document.body)}
        <div
        className="fixed bottom-6 right-6 z-[9999] cursor-pointer"
        onClick={() => setMinimized(false)}
      >
        <div
          className={`flex items-center gap-3 text-white px-4 py-3 rounded-full shadow-2xl transition-all duration-200 ${
            needsDisposition
              ? 'bg-amber-600 hover:bg-amber-700'
              : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          <PhoneIcon className={`h-5 w-5 ${needsDisposition ? '' : 'animate-pulse'}`} />
          <span className="font-semibold text-sm">
            {needsDisposition ? 'Disposition needed' : formatDuration(durationSeconds)}
          </span>
          {leadInfo?.leadName && (
            <span className="text-sm opacity-90 max-w-[120px] truncate">{leadInfo.leadName}</span>
          )}
          {/* A minimized agent still needs to know why they are hearing nothing. */}
          {isActive && !voicemailDetected && remoteAudio?.state === 'no-media' && (
            <span className="text-[10px] font-bold uppercase tracking-wide bg-red-600 text-white px-1.5 py-0.5 rounded-full">
              ⚠ No audio
            </span>
          )}
          {isActive && !voicemailDetected && remoteAudio?.state === 'silent' && (
            <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-400 text-amber-950 px-1.5 py-0.5 rounded-full">
              🔇 Silent
            </span>
          )}
          {pendingDispositionCount > 0 && (
            <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-400 text-amber-950 px-1.5 py-0.5 rounded-full">
              {pendingDispositionCount} pending
            </span>
          )}
        </div>
      </div>
      </>
    );
  }

  // ─── Full popup ────────────────────────────────────────────────────
  return (
    <>
    {followUpModal && createPortal(followUpModal, document.body)}
    <div
      ref={popupRef}
      className="fixed bottom-6 right-6 z-[9999] w-[380px] max-h-[calc(100vh-3rem)] animate-in slide-in-from-bottom-4 fade-in duration-300"
    >
      <div className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[calc(100vh-3rem)]">
        {/* ─── Header (stays visible) ─────────────────────────────── */}
        <div className="shrink-0 bg-gradient-to-r from-green-600 to-green-700 dark:from-green-700 dark:to-green-800 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <PhoneIcon className="h-5 w-5 text-white" />
              <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 bg-red-500 rounded-full animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-white text-sm font-semibold">Active Call</span>
                {voicemailDetected ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-amber-500/90 text-white">
                    📠 Voicemail
                  </span>
                ) : isActive && remoteAudio ? (
                  <LineAudioChip reading={remoteAudio} />
                ) : null}
              </div>
              <div className="text-green-100 text-xs font-mono">{formatDuration(durationSeconds)}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setMinimized(true)}
              className="p-1.5 rounded-lg text-green-100 hover:text-white hover:bg-white/10 transition-colors"
              title="Minimize"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 14H5" />
              </svg>
            </button>
          </div>
        </div>

        {pendingDispositionCount > 0 && (
          <div className="shrink-0 px-5 py-1.5 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-xs font-medium">
            {pendingDispositionCount} pending disposition{pendingDispositionCount === 1 ? '' : 's'}
          </div>
        )}

        {/* Scrollable body — prevents the panel from being cut off at the top
            on short (laptop) screens; the header above stays fixed. */}
        <div className="min-h-0 flex-1 overflow-y-auto">

        {/* ─── Voicemail Warning Banner ─────────────────────────── */}
        {voicemailDetected && isActive && (
          <div className="bg-amber-500 dark:bg-amber-600 px-5 py-2.5 flex items-center gap-3">
            <svg className="h-5 w-5 text-white flex-shrink-0 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <div>
              <div className="text-white text-sm font-bold">Voicemail Detected</div>
              <div className="text-amber-100 text-xs">This call went to voicemail — consider ending the call</div>
            </div>
          </div>
        )}

        {/* ─── Silent / broken line banner ──────────────────────── */}
        {/* Only when the panel is open on a live call, and never on top of the
            voicemail banner — one warning at a time, or agents read neither. */}
        {isActive && !voicemailDetected && remoteAudio?.state === 'no-media' && (
          <div className="bg-red-600 px-5 py-2.5 flex items-center gap-3">
            <svg className="h-5 w-5 text-white flex-shrink-0 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <div>
              <div className="text-white text-sm font-bold">No audio reaching you</div>
              <div className="text-red-100 text-xs">
                The connection to this call has stopped carrying sound. Hang up and take the
                next call — if it happens again, reload the page.
              </div>
            </div>
          </div>
        )}

        {isActive && !voicemailDetected && remoteAudio?.state === 'silent' && (
          <div className="bg-amber-500 dark:bg-amber-600 px-5 py-2.5 flex items-center gap-3">
            <svg className="h-5 w-5 text-white flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 9.75 19.5 12m0 0 2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.5a1.5 1.5 0 0 1-1.5-1.5v-4.5a1.5 1.5 0 0 1 1.5-1.5h2.25Z" />
            </svg>
            <div>
              <div className="text-white text-sm font-bold">
                Silent line — {Math.floor((remoteAudio.quietForMs || 0) / 1000)}s with no sound
              </div>
              <div className="text-amber-100 text-xs">
                The call is connected and audio is flowing, but nothing is coming from the
                merchant. Usually voicemail or nobody there.
              </div>
            </div>
          </div>
        )}

        {/* ─── Lead Info ──────────────────────────────────────────── */}
        <div className="px-4 py-2.5 space-y-1.5">
          {/* Merchant */}
          <div className="flex items-center gap-2 min-w-0">
            <BuildingStorefrontIcon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            <span className="text-xs text-muted-foreground flex-shrink-0 w-14">Merchant</span>
            {editingField === 'company' ? (
              <input
                autoFocus
                value={editableCompanyName}
                onChange={(e) => setEditableCompanyName(e.target.value)}
                onBlur={() => setEditingField(null)}
                onKeyDown={(e) => e.key === 'Enter' && setEditingField(null)}
                placeholder="Merchant name"
                className="flex-1 px-1.5 py-0.5 text-xs border border-primary/40 rounded bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            ) : (
              <span
                onClick={() => setEditingField('company')}
                title="Click to edit"
                className="flex-1 text-xs font-semibold text-foreground truncate cursor-text hover:text-primary transition-colors"
              >
                {editableCompanyName || <span className="text-muted-foreground font-normal italic">—</span>}
              </span>
            )}
          </div>

          {/* Deal */}
          {leadInfo?.campaignName && (
            <div className="flex items-center gap-2 min-w-0">
              <BriefcaseIcon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <span className="text-xs text-muted-foreground flex-shrink-0 w-14">Deal</span>
              <span className="flex-1 text-xs font-semibold text-foreground truncate">{leadInfo.campaignName}</span>
            </div>
          )}

          {/* Contact */}
          <div className="flex items-center gap-2 min-w-0">
            <svg className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
            <span className="text-xs text-muted-foreground flex-shrink-0 w-14">Contact</span>
            {editingField === 'contact' ? (
              <input
                autoFocus
                value={editableLeadName}
                onChange={(e) => setEditableLeadName(e.target.value)}
                onBlur={() => setEditingField(null)}
                onKeyDown={(e) => e.key === 'Enter' && setEditingField(null)}
                placeholder="Contact name"
                className="flex-1 px-1.5 py-0.5 text-xs border border-primary/40 rounded bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            ) : (
              <span
                onClick={() => setEditingField('contact')}
                title="Click to edit"
                className="flex-1 text-xs font-semibold text-foreground truncate cursor-text hover:text-primary transition-colors"
              >
                {editableLeadName || <span className="text-muted-foreground font-normal italic">—</span>}
              </span>
            )}
          </div>

          {/* Phone */}
          <div className="flex items-center gap-2 min-w-0">
            <PhoneIcon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            <span className="text-xs text-muted-foreground flex-shrink-0 w-14">Phone</span>
            <span className="flex-1 text-xs font-semibold text-foreground truncate">{formatPhone(leadInfo?.leadPhone || null)}</span>
          </div>

          {/* Email */}
          <div className="flex items-center gap-2 min-w-0">
            <EnvelopeIcon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            <span className="text-xs text-muted-foreground flex-shrink-0 w-14">Email</span>
            {editingField === 'email' ? (
              <input
                autoFocus
                type="email"
                value={editableLeadEmail}
                onChange={(e) => setEditableLeadEmail(e.target.value)}
                onBlur={() => setEditingField(null)}
                onKeyDown={(e) => e.key === 'Enter' && setEditingField(null)}
                placeholder="Email address"
                className="flex-1 px-1.5 py-0.5 text-xs border border-primary/40 rounded bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            ) : (
              <span
                onClick={() => setEditingField('email')}
                title="Click to edit"
                className="flex-1 text-xs font-semibold text-foreground truncate cursor-text hover:text-primary transition-colors"
              >
                {editableLeadEmail || <span className="text-muted-foreground font-normal italic">—</span>}
              </span>
            )}
          </div>

          {/* Lead Notes */}
          <div className="flex items-start gap-2 min-w-0">
            <PencilSquareIcon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <span className="text-xs text-muted-foreground flex-shrink-0 w-14 pt-0.5">Notes</span>
            {editingNotes ? (
              <textarea
                autoFocus
                value={leadNotesDraft}
                onChange={(e) => setLeadNotesDraft(e.target.value)}
                onBlur={() => setEditingNotes(false)}
                rows={3}
                maxLength={5000}
                placeholder="Notes about this lead…"
                className="flex-1 px-1.5 py-0.5 text-xs border border-primary/40 rounded bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
              />
            ) : (
              <span
                onClick={() => setEditingNotes(true)}
                title="Click to edit notes"
                className="flex-1 text-xs text-foreground cursor-text hover:text-primary transition-colors leading-relaxed whitespace-pre-wrap break-words"
              >
                {leadNotesDraft || <span className="text-muted-foreground italic">—</span>}
              </span>
            )}
          </div>

          {/* Save — only visible when there are unsaved edits */}
          {hasLeadDetailEdits && (
            <div className="flex justify-end pt-0.5">
              <button
                disabled={!leadInfo?.leadId || savingLeadDetails}
                onClick={handleSaveLeadDetails}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {savingLeadDetails ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}

          {/* Fallback when no lead info yet */}
          {!leadInfo && (
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <ClockIcon className="h-3.5 w-3.5" />
              Loading lead information…
            </div>
          )}
        </div>

        <div className="border-t border-border" />

        {/* ─── Call Controls ──────────────────────────────────────── */}
        {isActive && (
          <div className="px-5 py-3 flex items-center justify-center gap-4 border-b border-border">
            {/* Mute Button */}
            <button
              onClick={onToggleMute}
              className={`
                flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200
                ${isMuted
                  ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted border border-border'
                }
              `}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              <MicrophoneIcon className="h-4 w-4" />
              {isMuted ? 'Unmute' : 'Mute'}
              {isMuted && (
                <XMarkIcon className="h-3 w-3 -ml-0.5" />
              )}
            </button>

            {/* Hang Up Button */}
            <button
              onClick={onHangUp}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-all duration-200 shadow-lg shadow-red-500/20"
            >
              <PhoneXMarkIcon className="h-4 w-4" />
              End Call
            </button>
          </div>
        )}

        {/* ─── Dispositions ───────────────────────────────────────── */}
        {showDispositions && (
        <div className="px-5 py-4">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
            {isResultsCallback
              ? 'Call outcome (does not change lead stage)'
              : isActive
                ? 'Set Disposition (optional before hang up)'
                : 'Disposition'}
          </div>

          {dispositionSubmitted ? (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
              <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-700 dark:text-green-400">
                Marked as:{' '}
                {visibleDispositions.find((d) => d.value === dispositionSubmitted)?.label ||
                  dispositions.find((d) => d.value === dispositionSubmitted)?.label ||
                  dispositionSubmitted}
              </span>
            </div>
          ) : (
            <>
              {!isResultsCallback && (
              <>
              <div className="mb-3">
                <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                  Main Stage
                </label>
                {isPipelineMainStatus(leadInfo?.leadStatus) && !mainStatusChange && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Current: {MAIN_STATUS_LABELS[leadInfo.leadStatus]}
                  </p>
                )}
                <select
                  className="input w-full text-xs mt-1"
                  value={mainStatusChange}
                  onChange={(e) => setMainStatusChange(e.target.value)}
                >
                  <option value="">
                    {isPipelineMainStatus(leadInfo?.leadStatus)
                      ? `Keep current (${MAIN_STATUS_LABELS[leadInfo.leadStatus]})`
                      : 'Select main status'}
                  </option>
                  {PIPELINE_MAIN_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {MAIN_STATUS_LABELS[status]}
                    </option>
                  ))}
                </select>
                {!isMainDial && !effectiveMainStatus && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1.5">
                    Select a main status before choosing a sub-stage.
                  </p>
                )}
              </div>

              {!isMainDial && (subStages.length > 0 || effectiveMainStatus) && (
                <div className="mb-3">
                  <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Sub-Stages
                    {effectiveMainStatus && (
                      <span className="normal-case font-normal ml-1">
                        for {MAIN_STATUS_LABELS[effectiveMainStatus as PipelineMainStatus]}
                      </span>
                    )}
                  </div>
                  {!effectiveMainStatus ? (
                    <p className="text-[11px] text-muted-foreground">
                      Sub-stages unlock after a main status is selected.
                    </p>
                  ) : subStages.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground">
                      No sub-stages configured for this main status.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {subStages.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          disabled={submitting || !effectiveMainStatus}
                          onClick={() => void handleSubStageSelect(s.id)}
                          className="text-xs px-2.5 py-1 rounded-lg border border-border bg-muted/40 hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {s.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              </>
              )}

              <div className={`grid gap-2 ${isResultsCallback ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {visibleDispositions.map((d) => (
                  <button
                    key={d.value}
                    disabled={submitting}
                    onClick={() => handleDisposition(d.value)}
                    className={`
                      flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium border transition-all duration-200
                      ${d.bgColor} ${d.color} ${d.hoverColor}
                      disabled:opacity-50 disabled:cursor-not-allowed
                      active:scale-95
                    `}
                  >
                    <span className="text-base leading-none">{d.icon}</span>
                    {d.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        )}
        </div>
      </div>
    </div>
    </>
  );
};

export default InCallPopup;
